import { pointable } from './step-geometry'

/**
 * Can the tour point at it? It has layout (display:none / detached elements
 * report an empty rect) and enough of it is on-canvas sideways — the same
 * `visibleEnough` rule the geometry validator applies (a 3 px sliver clipped
 * at the edge is not enough). Vertically off-screen is fine — the host
 * scrolls it into view.
 */
export function isVisible(el: Element): boolean {
  return pointable(el)
}

/** In the DOM now, but not something the tour can point at. */
export function isHiddenNow(selector: string): boolean {
  const el = document.querySelector(selector)
  return !!el && !isVisible(el)
}

/**
 * Resolve the first VISIBLE match for `selector`, or `null` after `timeoutMs`.
 *
 * - Absent element: wait (MutationObserver) up to `timeoutMs` — covers lazy
 *   chunks and data loads after a route change.
 * - Present but hidden (e.g. the sidebar under the narrow-screen breakpoint):
 *   give it `hiddenGraceMs` to appear, then give up — CSS-hidden targets do
 *   not come back on their own, so waiting the full window just stalls.
 * - Abort via `signal`. Observer and timers are always released.
 */
export function waitForTarget(
  selector: string,
  timeoutMs: number,
  signal: AbortSignal,
  { hiddenGraceMs = 400, visible = isVisible }: { hiddenGraceMs?: number; visible?: (el: Element) => boolean } = {},
): Promise<Element | null> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve(null)

    let done = false
    let graceTimer: ReturnType<typeof setTimeout> | undefined
    // Present-but-hidden can turn visible with no DOM mutation at all (a window
    // resize settling, a pane re-laying out): re-check on resize and on a slow poll.
    const poll = setInterval(() => void check(), 300)
    const onResize = () => void check()
    window.addEventListener('resize', onResize)
    const observer = new MutationObserver(() => void check())
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined

    const finish = (el: Element | null) => {
      if (done) return
      done = true
      observer.disconnect()
      clearInterval(poll)
      window.removeEventListener('resize', onResize)
      clearTimeout(timeout)
      clearTimeout(graceTimer)
      signal.removeEventListener('abort', onAbort)
      resolve(el)
    }
    const onAbort = () => finish(null)

    const check = (): boolean => {
      const el = document.querySelector(selector)
      if (el && el.isConnected && visible(el)) {
        finish(el)
        return true
      }
      if (el && graceTimer === undefined) {
        graceTimer = setTimeout(() => finish(null), hiddenGraceMs)
      } else if (!el && graceTimer !== undefined) {
        clearTimeout(graceTimer)
        graceTimer = undefined
      }
      return false
    }

    if (check()) return
    signal.addEventListener('abort', onAbort, { once: true })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'data-tour'],
    })
    timeout = setTimeout(() => finish(null), timeoutMs)
  })
}
